<script>
  import { onMount } from 'svelte';
  import {
    COLORS, buildScales, clearCanvas, drawGrid, drawTimeLabels,
    drawCrosshair, indexFromX, summarize,
  } from './chartUtils.js';
  import { formatCurrency } from '../../../lib/store.js';

  // Props, not a global store (a deliberate departure — backtest series are component-scoped; KTD/U6).
  export let data = [];      // [{ time, value, cash }] oldest->newest
  export let trades = [];     // [{ symbol, side, tradeTime, price, qty }]

  let canvas;
  let ctx;
  let container;
  let width = 600;
  let height = 280;
  let hoverIndex = -1;

  const fmt = (v) => formatCurrency(v, 0);
  $: hasData = Array.isArray(data) && data.length >= 2;
  $: summary = summarize(data, 'Equity curve', fmt);

  // Map each trade to the nearest equity index by time, computed ONCE per data/trades change (not on every
  // hover redraw). The equity curve is time-sorted oldest->newest, so a binary search beats a linear scan.
  $: markers = computeMarkers(data, trades);
  function computeMarkers(curve, tradeList) {
    if (!Array.isArray(curve) || curve.length < 2 || !Array.isArray(tradeList)) return [];
    const times = curve.map((p) => new Date(p.time).getTime());
    return tradeList
      .map((t) => ({ tt: new Date(t.tradeTime).getTime(), side: String(t.side).toLowerCase() }))
      // Skip trades with an unparseable time — a NaN target would otherwise pin a false marker at index 0.
      .filter((t) => !Number.isNaN(t.tt))
      .map((t) => ({ index: nearestIndex(times, t.tt), side: t.side }));
  }

  // Nearest index to `target` in a sorted-ascending array (binary search).
  function nearestIndex(sorted, target) {
    let lo = 0;
    let hi = sorted.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (sorted[mid] < target) lo = mid + 1;
      else hi = mid;
    }
    if (lo > 0 && Math.abs(sorted[lo - 1] - target) <= Math.abs(sorted[lo] - target)) return lo - 1;
    return lo;
  }

  function resize() {
    if (!container) return;
    width = Math.max(200, container.offsetWidth);
    height = 280;
  }

  function draw() {
    if (!ctx || !hasData) return;
    clearCanvas(ctx, width, height);
    const scales = buildScales(data, width, height);
    drawGrid(ctx, width, height, scales, fmt);
    drawTimeLabels(ctx, data, height, scales);

    // Equity line.
    ctx.strokeStyle = COLORS.line;
    ctx.lineWidth = 2;
    ctx.beginPath();
    data.forEach((p, i) => {
      const x = scales.xOf(i);
      const y = scales.yOf(p.value);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Trade markers: buy = up-triangle (gain), sell = down-triangle (loss). Shape + color (not color only).
    for (const m of markers) {
      const x = scales.xOf(m.index);
      const y = scales.yOf(data[m.index].value);
      ctx.fillStyle = m.side === 'sell' ? COLORS.loss : COLORS.gain;
      ctx.beginPath();
      if (m.side === 'sell') {
        ctx.moveTo(x, y + 7); ctx.lineTo(x - 4, y); ctx.lineTo(x + 4, y);
      } else {
        ctx.moveTo(x, y - 7); ctx.lineTo(x - 4, y); ctx.lineTo(x + 4, y);
      }
      ctx.closePath();
      ctx.fill();
    }

    if (hoverIndex >= 0) {
      drawCrosshair(ctx, data, hoverIndex, width, height, scales, fmt);
    }
  }

  // Redraw on the PROP (not a store) per the KTD guidance; never afterUpdate (CPU feedback loop).
  // Depend on `data` and `markers` directly (not just `hasData`): switching between two non-empty historical
  // runs leaves `hasData` === true, so a guard keyed only on hasData would never re-run and the canvas would
  // keep the prior run's drawing. Referencing data/markers makes Svelte re-run draw() when the series changes.
  $: if (ctx && canvas && hasData && width && height && data && markers && (hoverIndex || hoverIndex === -1)) {
    draw();
  }

  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    hoverIndex = indexFromX(e.clientX - rect.left, data.length, width);
  }
  function onLeave() { hoverIndex = -1; }

  // Keyboard crosshair (a11y): focusing the canvas + arrow keys steps points; aria-live announces each.
  let liveMsg = '';
  function onKey(e) {
    if (!hasData) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const start = hoverIndex < 0 ? 0 : hoverIndex;
      hoverIndex = Math.max(0, Math.min(data.length - 1, start + (e.key === 'ArrowRight' ? 1 : -1)));
      const p = data[hoverIndex];
      const d = new Date(p.time);
      liveMsg = `${Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString()}: ${fmt(p.value)}`;
    }
  }

  // Lazy 2D-context acquisition: the canvas renders inside {#if hasData}, so it may not exist at
  // mount (empty data — acquiring unconditionally threw and broke the whole reactive graph) and may
  // APPEAR later when data arrives. Acquire the context whenever the canvas binds.
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  onMount(() => {
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    let pendingDraw;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && ctx && canvas) {
          clearTimeout(pendingDraw);
          pendingDraw = setTimeout(() => { resize(); draw(); }, 50);
        }
      });
    }, { threshold: 0.1 });
    if (container) observer.observe(container);
    // Clear the pending deferred draw so it can't fire on a detached canvas after unmount.
    return () => { window.removeEventListener('resize', onResize); observer.disconnect(); clearTimeout(pendingDraw); };
  });
</script>

<div class="bt-chart" bind:this={container}>
  <h4 class="bt-chart-title">Equity Curve</h4>
  {#if hasData}
    <canvas
      bind:this={canvas}
      width={width}
      height={height}
      tabindex="0"
      aria-label={summary}
      on:mousemove={onMove}
      on:mouseleave={onLeave}
      on:keydown={onKey}
    ></canvas>
    <div class="bt-chart-live" aria-live="polite">{liveMsg}</div>
    <details class="bt-chart-data">
      <summary>View data as table</summary>
      <table>
        <thead><tr><th>Time</th><th>Equity</th><th>Cash</th></tr></thead>
        <tbody>
          {#each data as p}
            <tr><td>{new Date(p.time).toLocaleDateString()}</td><td>{fmt(p.value)}</td><td>{fmt(p.cash)}</td></tr>
          {/each}
        </tbody>
      </table>
    </details>
  {:else}
    <div class="empty-state"><p class="empty-state-description">Insufficient data for this chart.</p></div>
  {/if}
</div>

<style>
  @import './charts.css';
</style>
